import {
  IconChevronDown,
  IconChevronUp,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconMenu2,
} from "@tabler/icons-react";
import {Layout, Tooltip} from "@ui";
import PropTypes from "prop-types";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import bgamingAvatar from "../../assets/images/AVATAR_PROVEEDORES/BGAMING_AVATAR.jpg";
import evolutionAvatar from "../../assets/images/AVATAR_PROVEEDORES/EVOLUTION_AVATAR.jpg";
import evoplayAvatar from "../../assets/images/AVATAR_PROVEEDORES/EVOPLAY_AVATAR.jpg";
import hacksawAvatar from "../../assets/images/AVATAR_PROVEEDORES/HACKSAW_AVATAR.jpg";
import kagaAvatar from "../../assets/images/AVATAR_PROVEEDORES/kaga_logo.jpg";
import pixmoveAvatar from "../../assets/images/AVATAR_PROVEEDORES/PIXMOVE_AVATAR.jpg";
import pragmaticAvatar from "../../assets/images/AVATAR_PROVEEDORES/PRAGMATIC_AVATAR.jpg";
import tadaAvatar from "../../assets/images/AVATAR_PROVEEDORES/TADA_AVATAR.jpg";
import upgamingAvatar from "../../assets/images/AVATAR_PROVEEDORES/UPGAMING_AVATAR.jpg";
import gamingcorpsLogo from "../../assets/images/providers-logos/gamingcorps.svg";
import {useAuthStore} from "../../store/useAuth.store";
import styles from "./CustomNavbar.module.css";
import CategoryChip from "./components/CategoryChip";
import ProvidersGrid from "./components/ProvidersGrid";
import SidebarUserCard from "./components/SidebarUserCard";

const {Sider} = Layout;

// Sidebar provider cards — fixed order per design
const sidebarProviders = [
  {code: "evoplay", label: "Evoplay", avatar: evoplayAvatar, key: "/games/provider/evoplay"},
  {code: "bgaming", label: "BGaming", avatar: bgamingAvatar, key: "/games/provider/bgaming"},
  {code: "tadagaming", label: "Tada Game", avatar: tadaAvatar, key: "/games/provider/tadagaming"},
  {code: "evolution", label: "Evolution", avatar: evolutionAvatar, key: "/games/provider/evolution"},
  {code: "pragmatic", label: "Pragmatic", avatar: pragmaticAvatar, key: "/games/provider/pragmatic"},
  {code: "kagaming", label: "KA Games", avatar: kagaAvatar, key: "/games/provider/kaga"},
  {code: "upgaming", label: "Up Gaming", avatar: upgamingAvatar, key: "/games/provider/upgaming"},
  {code: "hacksaw", label: "Hacksaw", avatar: hacksawAvatar, key: "/games/provider/hacksaw"},
  {code: "gamingcorps", label: "Gaming Corps", avatar: gamingcorpsLogo, key: "/games/provider/gamingcorps"},
  {code: "pixmove", label: "Pix Move", avatar: pixmoveAvatar, key: "/games/provider/pixmove"},
];

const CustomNavbar = ({
  items,
  onSelect,
  collapsed,
  onCollapse,
  mode = "vertical",
  logo,
  monogram,
  onLogoClick,
  className,
  style,
  overflowedIndicator,
  selectedKeys = [],
  defaultOpenKeys = [],
}) => {
  const [openKeys, setOpenKeys] = useState(defaultOpenKeys);
  const [activeKey, setActiveKey] = useState(selectedKeys[0] || "");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);
  const {t} = useTranslation();
  const navigate = useNavigate();

  const authStatus = useAuthStore(state => state.authStatus);

  useEffect(() => {
    const handleClickOutside = event => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleItemClick = (item, event) => {
    // Only prevent default for navigation items
    if (!item.onClick) {
      event.preventDefault();
    }

    if (item.children) {
      // Handle dropdown toggle - don't navigate for dropdown items
      if (openKeys.includes(item.key)) {
        setOpenKeys(openKeys.filter(key => key !== item.key));
      } else {
        setOpenKeys([...openKeys, item.key]);
      }
      setActiveKey(item.key);
      // Don't call onSelect for dropdown items - only for actual navigation items
    } else {
      // Handle menu item selection
      setActiveKey(item.key);
      if (item.onClick) {
        item.onClick();
      } else {
        onSelect?.(item); // Pass the full item, not just key
      }
      setShowMobileMenu(false);
    }
  };

  const renderIcon = icon => {
    if (React.isValidElement(icon)) {
      return icon;
    }
    return null;
  };

  const renderJuegosDropdown = children => {
    // Find providers and category children
    const providersChild = children.find(child => child.key === "providers");
    const categoryChild = children.find(child => child.key === "category");

    const handleAllGamesClick = () => {
      setActiveKey("");
      navigate("/gameslist/all", {
        state: {
          resetLocalFilters: true,
          fromAllGamesButtonAt: Date.now(),
        },
      });
      setShowMobileMenu(false);
    };

    return (
      <div className={styles.juegoDropdown}>
        {/* See All Button - moved to top */}
        <div style={{display: "flex", justifyContent: "center", marginBottom: 16}}>
          <button
            type="button"
            className={styles.allOptionsBtn}
            onClick={handleAllGamesClick}
          >
            {t("allPageGames")}
          </button>
        </div>

        {providersChild?.children && (
          <div
            key={providersChild.key}
            className={styles.providersSection}
          >
            <div className={styles.providersTitle}>{providersChild.label}</div>
            <ProvidersGrid
              providers={sidebarProviders}
              onItemClick={handleItemClick}
            />
          </div>
        )}
        {/* Divider between providers and categories */}
        {categoryChild && (
          <div
            key={categoryChild.key}
            className={styles.categoriesSection}
          >
            <div className={styles.categoriesTitle}>{categoryChild.label}</div>
            <div className={styles.categoriesGrid}>
              {categoryChild.children.map((category, index) => {
                const categoryKey = category.key || `category-${index}`;
                return (
                  <CategoryChip
                    key={categoryKey}
                    category={category}
                    isActive={activeKey === category.key}
                    onClick={e => handleItemClick(category, e)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMenuItem = (item, level = 0) => {
    const isOpen = openKeys.includes(item.key);
    const isActive = activeKey === item.key;
    const hasChildren = item.children && item.children.length > 0;

    // Ensure item has a key
    const itemKey = item.key || `item-${level}-${Math.random().toString(36).substr(2, 9)}`;

    const itemType = (() => {
      if (item.label === t("sports") || item.key === "sports") return "sports";
      if (item.label === t("lottery") || item.key === "lottery") return "lottery";
      return "other";
    })();
    const isJuegosDropdown = item.key === "games" && hasChildren;
    const isOpenSubmenu = (isJuegosDropdown || itemType === "sports" || itemType === "lottery") && isOpen && !collapsed;

    const isSpecialGrid = itemType === "sports" || itemType === "lottery";

    return (
      <li
        key={itemKey}
        className={`${styles.menuItem} ${isOpenSubmenu ? styles.subMenuItem : ""}`}
      >
        {collapsed && level === 0 && !hasChildren ? (
          <Tooltip
            title={item.label}
            placement="right"
            mouseEnterDelay={0.5}
          >
            <div
              className={`${styles.menuItemContent} ${isActive ? styles.active : ""} ${isOpen ? styles.opened : ""} ${
                level > 0 ? styles.submenuItem : ""
              }`}
              onClick={e => handleItemClick(item, e)}
              style={{paddingLeft: `${level * 20 + 16}px`}}
            >
              {item.icon && <span className={styles.menuIcon}>{renderIcon(item.icon)}</span>}
              <span className={`${styles.menuText} ${collapsed && level === 0 ? styles.hidden : ""}`}>
                {item.label}
              </span>
            </div>
          </Tooltip>
        ) : (
          <div
            className={`${styles.menuItemContent} ${isActive ? styles.active : ""} ${isOpen ? styles.opened : ""} ${
              level > 0 ? styles.submenuItem : ""
            } ${isOpenSubmenu ? styles.subMenuItemActive : ""}`}
            onClick={e => handleItemClick(item, e)}
            style={{paddingLeft: `${level * 20 + 16}px`}}
          >
            {item.icon && <span className={styles.menuIcon}>{renderIcon(item.icon)}</span>}
            <span className={`${styles.menuText} ${collapsed && level === 0 ? styles.hidden : ""}`}>{item.label}</span>
            {hasChildren && !collapsed && (
              <span className={styles.menuArrow}>
                {isOpen ? (
                  <IconChevronUp
                    size={16}
                    stroke={2}
                    color="var(--theme-text-soft)"
                  />
                ) : (
                  <IconChevronDown
                    size={16}
                    stroke={2}
                    color="var(--theme-text-soft)"
                  />
                )}
              </span>
            )}
          </div>
        )}
        {hasChildren && isOpen && !collapsed && (
          <ul
            className={`${styles.submenu} ${
              isJuegosDropdown ? styles.juegoSubmenu : ""
            } ${itemType === "sports" ? styles.sportsSubmenu : ""} ${itemType === "lottery" ? styles.lotterySubmenu : ""}`}
            data-debug-sports-submenu={
              typeof item.key === "string" && item.key.startsWith("/sports") ? "true" : undefined
            }
          >
            {isJuegosDropdown ? (
              renderJuegosDropdown(item.children)
            ) : item.children.some(child => child.children) ? (
              // If any child has children, render with categories
              item.children.map(child =>
                child.children ? (
                  <li
                    key={child.key}
                    className={styles.submenuCategory}
                  >
                    <div className={styles.categoryTitle}>{child.label}</div>
                    <div className={styles.categoryItems}>
                      {child.children.map(grandChild => (
                        <div
                          key={grandChild.key}
                          className={styles.submenuItem}
                          onClick={e => handleItemClick(grandChild, e)}
                        >
                          {grandChild.icon && <span className={styles.menuIcon}>{renderIcon(grandChild.icon)}</span>}
                          <span className={styles.menuText}>{grandChild.label} </span>
                        </div>
                      ))}
                    </div>
                  </li>
                ) : (
                  <li
                    key={child.key}
                    className={styles.submenuItem}
                    onClick={e => handleItemClick(child, e)}
                  >
                    {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                    <span className={styles.menuText}>{child.label} </span>
                  </li>
                )
              )
            ) : isSpecialGrid ? (
              <div className={itemType === "sports" ? styles.sportsSubmenuGrid : styles.lotterySubmenuGrid}>
                {item.children.map(child => {
                  let isAllButton = false;
                  if (itemType === "sports") {
                    isAllButton = child.key === "/sports";
                  } else if (itemType === "lottery") {
                    const label = typeof child.label === "string" ? child.label.toLowerCase() : "";
                    const keyStr = typeof child.key === "string" ? child.key.toLowerCase() : "";
                    isAllButton = label.includes("todos") || label.includes("all") || keyStr.includes("all");
                  }
                  if (isAllButton) {
                    return (
                      <div
                        key={child.key}
                        className={`${styles.submenuItem} ${styles.sportsAllBtnContainer}`}
                      >
                        <button
                          type="button"
                          className={styles.allOptionsBtn}
                          onClick={e => handleItemClick(child, e)}
                        >
                          {child.label}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={child.key}
                      className={styles.submenuItem}
                      onClick={e => handleItemClick(child, e)}
                    >
                      {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                      <span className={styles.menuText}>{child.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.submenuGrid}>
                {item.children.map(child => (
                  <div
                    key={child.key}
                    className={styles.submenuItem}
                    onClick={e => handleItemClick(child, e)}
                  >
                    {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                    <span className={styles.menuText}>{child.label}</span>
                  </div>
                ))}
              </div>
            )}
          </ul>
        )}
        {hasChildren && collapsed && (
          <ul
            className={
              `${styles.collapsedSubmenu} ` +
              (itemType === "sports" ? styles.sportsSubmenu : itemType === "lottery" ? styles.lotterySubmenu : "")
            }
          >
            {isJuegosDropdown ? (
              renderJuegosDropdown(item.children)
            ) : item.children.some(child => child.children) ? (
              // If any child has children, render with categories
              item.children.map(child =>
                child.children ? (
                  <li
                    key={child.key}
                    className={styles.submenuCategory}
                  >
                    <div className={styles.categoryTitle}>{child.label}</div>
                    <div className={styles.categoryItems}>
                      {child.children.map(grandChild => (
                        <div
                          key={grandChild.key}
                          className={styles.submenuItem}
                          onClick={e => handleItemClick(grandChild, e)}
                        >
                          {grandChild.icon && <span className={styles.menuIcon}>{renderIcon(grandChild.icon)}</span>}
                          <span className={styles.menuText}>{grandChild.label}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                ) : (
                  <li
                    key={child.key}
                    className={styles.submenuItem}
                    onClick={e => handleItemClick(child, e)}
                  >
                    {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                    <span className={styles.menuText}>{child.label}</span>
                  </li>
                )
              )
            ) : isSpecialGrid ? (
              <div className={itemType === "sports" ? styles.sportsSubmenuGrid : styles.lotterySubmenuGrid}>
                {item.children.map(child => {
                  let isAllButton = false;
                  if (itemType === "sports") {
                    isAllButton = child.key === "/sports";
                  } else if (itemType === "lottery") {
                    const label = typeof child.label === "string" ? child.label.toLowerCase() : "";
                    const keyStr = typeof child.key === "string" ? child.key.toLowerCase() : "";
                    isAllButton = label.includes("todos") || label.includes("all") || keyStr.includes("all");
                  }
                  if (isAllButton) {
                    return (
                      <div
                        key={child.key}
                        className={`${styles.submenuItem} ${styles.sportsAllBtnContainer}`}
                      >
                        <button
                          type="button"
                          className={styles.allOptionsBtn}
                          onClick={e => handleItemClick(child, e)}
                        >
                          {child.label}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={child.key}
                      className={styles.submenuItem}
                      onClick={e => handleItemClick(child, e)}
                    >
                      {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                      <span className={styles.menuText}>{child.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.submenuGrid}>
                {item.children.map(child => (
                  <div
                    key={child.key}
                    className={styles.submenuItem}
                    onClick={e => handleItemClick(child, e)}
                  >
                    {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                    <span className={styles.menuText}>{child.label}</span>
                  </div>
                ))}
              </div>
            )}
          </ul>
        )}
      </li>
    );
  };

  const renderHorizontalMenuItem = item => {
    const isActive = activeKey === item.key;
    const hasChildren = item.children && item.children.length > 0;

    // Ensure item has a key
    const itemKey = item.key || `horizontal-item-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <li
        key={itemKey}
        className={styles.horizontalMenuItem}
      >
        <div
          className={`${styles.horizontalMenuItemContent} ${isActive ? styles.active : ""}`}
          onClick={e => handleItemClick(item, e)}
        >
          {item.icon && <span className={styles.menuIcon}>{renderIcon(item.icon)}</span>}
          <span className={styles.menuText}>{item.label}</span>
          {hasChildren && (
            <span className={styles.menuArrow}>
              <IconChevronDown
                size={16}
                stroke={2}
                color="var(--theme-text-soft)"
              />
            </span>
          )}
        </div>
        {hasChildren && (
          <ul className={styles.horizontalSubmenu}>
            {item.children.map(child => (
              <li
                key={child.key}
                className={styles.horizontalSubmenuItem}
              >
                <div
                  className={styles.horizontalSubmenuItemContent}
                  onClick={e => handleItemClick(child, e)}
                >
                  {child.icon && <span className={styles.menuIcon}>{renderIcon(child.icon)}</span>}
                  <span className={styles.menuText}>{child.label}</span>
                </div>
                {child.children && (
                  <ul className={styles.horizontalDeepSubmenu}>
                    {child.children.map(grandChild => (
                      <li
                        key={grandChild.key}
                        className={styles.horizontalSubmenuItem}
                      >
                        <div
                          className={styles.horizontalSubmenuItemContent}
                          onClick={e => handleItemClick(grandChild, e)}
                        >
                          <span className={styles.menuText}>{grandChild.label}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  if (mode === "vertical") {
    return (
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        trigger={null}
        width={260}
        collapsedWidth={78}
        style={{
          ...style,
          height: "100vh",
          margin: 0,
          padding: 0,
        }}
        className={className}
      >
        <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : styles.expanded}`}>
          {/* Collapse Trigger */}
          <div
            className={styles.collapseButton}
            onClick={onCollapse}
          >
            {collapsed ? (
              <IconLayoutSidebarLeftExpand
                size={24}
                stroke={2}
              />
            ) : (
              <IconLayoutSidebarLeftCollapse
                size={24}
                stroke={2}
              />
            )}
          </div>
          {/* Logo */}
          <div className={styles.logoContainer}>
            {collapsed && monogram && (
              <img
                src={monogram}
                alt="Logo"
                className={styles.monogram}
                onClick={onLogoClick}
              />
            )}
            {!collapsed && logo && (
              <img
                src={logo}
                alt="Logo"
                className={styles.logo}
                onClick={onLogoClick}
                style={{
                  width: "147.56px",
                  margin: "20px 20px 30px 0px",
                }}
              />
            )}
          </div>

          {/* Menu Items */}
          <nav className={styles.nav}>
            {/* User Card (authenticated only) */}
            {!collapsed && authStatus === "authenticated" && <SidebarUserCard />}
            <ul className={styles.menu}>{items.map(item => renderMenuItem(item, 0))}</ul>
          </nav>
        </div>
      </Sider>
    );
  }

  // Horizontal mode
  return (
    <nav
      className={`${styles.horizontalNav} ${className || ""}`}
      style={style}
    >
      <div className={styles.horizontalContainer}>
        {/* Mobile Menu Button */}
        <div
          className={styles.mobileMenuButton}
          ref={mobileMenuRef}
        >
          <button
            type="button"
            className={styles.mobileMenuTrigger}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            {overflowedIndicator || (
              <IconMenu2
                size={25}
                stroke={2}
                color="white"
              />
            )}
          </button>

          {showMobileMenu && (
            <div className={styles.mobileMenuDropdown}>
              <ul className={styles.mobileMenu}>{items.map(item => renderMenuItem(item, 0))}</ul>
            </div>
          )}
        </div>

        {/* Desktop Horizontal Menu */}
        <ul className={styles.horizontalMenu}>{items.map(item => renderHorizontalMenuItem(item))}</ul>
      </div>
    </nav>
  );
};

CustomNavbar.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelect: PropTypes.func,
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  mode: PropTypes.oneOf(["vertical", "horizontal"]),
  logo: PropTypes.string,
  monogram: PropTypes.string,
  onLogoClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  overflowedIndicator: PropTypes.node,
  selectedKeys: PropTypes.array,
  defaultOpenKeys: PropTypes.array,
};

export default CustomNavbar;
